/**
 * Real Keycloak login — ticket 86e232ykg.
 *
 * `oidc-client-ts` directly, no React wrapper (`@react-keycloak/web` is
 * deprecated/unmaintained — confirmed 2026-08-07). This file owns the
 * `UserManager`; the rest of the app keeps reading identity from
 * `useAuthStore` exactly as it does for the mock role picker in
 * `LoginModal.tsx` — this just becomes a second thing that can call
 * `setUser()`. No parallel state, no new context.
 *
 * Silent renewal uses the refresh-token grant, NOT an iframe
 * (`silent_redirect_uri` is deliberately unset). `UserManager.signinSilent()`
 * uses the refresh token automatically when the current user has one —
 * Keycloak issues one by default for the standard flow, no `offline_access`
 * scope needed. Iframe-based silent SSO checks are increasingly broken by
 * third-party-cookie blocking (Safari ITP, Chrome's phase-out); refresh
 * tokens sidestep that entirely.
 *
 * Verified against sandbox 2026-08-07:
 *   - Client `flysafe-frontend-airmarket`, realm `AIRmarket-Corp`, public,
 *     PKCE S256, Standard flow, redirect URI `<origin>/auth/callback`
 *     registered on the client by the team (Keycloak dashboard access
 *     confirmed).
 *   - Admin check per BE instruction: `realm_access.roles` includes
 *     `'tenant-admin'`. Do NOT use the `groups` claim for this — BE was
 *     explicit that `groups` (`/private-<tenant>/users` or `/admins`) is
 *     for tenant identity/isolation only, not a view-gating signal.
 *
 * UNCONFIRMED — flag before relying on this in anything but dev: BE's test
 * accounts only cover `user1`/`user2` (both presumably plain `user`) and
 * `admin1` (`tenant-admin`). No realm role for our `pilot` tier
 * (`inventory:write` / `surveillance:write` capabilities) was mentioned.
 * `PILOT_ROLE` below is a guess pending confirmation — anyone without the
 * admin role falls back to `user` (least-privilege default) until BE
 * confirms the real role name.
 */
import { config } from '@/lib/config';
import type { AuthUser } from '@/lib/store/auth';
import { useAuthStore } from '@/lib/store/auth';
import { type User, UserManager } from 'oidc-client-ts';
import { toast } from 'sonner';

const ADMIN_ROLE = 'tenant-admin';
/** UNCONFIRMED with BE — see file header. */
const PILOT_ROLE = 'tenant-pilot';

let userManager: UserManager | null = null;

/** Lazily constructs the singleton `UserManager`. Returns null if the
 *  feature flag is off or the realm/client aren't configured — every
 *  caller below is a no-op in that case rather than throwing, so an
 *  unconfigured build (e.g. a tenant without Keycloak set up yet) doesn't
 *  crash on import. */
export function getUserManager(): UserManager | null {
  if (!config.features.keycloakAuth) return null;
  if (!config.oidc.authority || !config.oidc.clientId) return null;
  if (userManager) return userManager;

  userManager = new UserManager({
    authority: config.oidc.authority,
    client_id: config.oidc.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    // 'roles' isn't a real OIDC scope — Keycloak includes realm_access.roles
    // in the token via a default protocol mapper on the client, no scope
    // needed to request it.
    scope: 'openid profile email',
    automaticSilentRenew: true,
    // No silent_redirect_uri — see file header re: refresh-token renewal.
    loadUserInfo: false,
  });

  userManager.events.addUserLoaded((u) => useAuthStore.getState().setUser(mapOidcUser(u)));
  userManager.events.addUserUnloaded(() => useAuthStore.getState().setUser(null));
  userManager.events.addSilentRenewError((err) => {
    // Refresh failed (expired/revoked refresh token, network blip). Drop the
    // local session rather than let the UI show a stale "signed in" state
    // against an access token that will start failing API calls.
    console.warn('[keycloak] silent renew failed, signing out locally', err);
    useAuthStore.getState().setUser(null);
    // Without this the UI just quietly drops to the public view.
    toast.info('Your session has expired. Please sign in again.');
  });

  return userManager;
}

/** Call once at app bootstrap. Restores a session from a prior visit
 *  (oidc-client-ts persists tokens in sessionStorage by default) without
 *  forcing a fresh redirect, and is the single source of truth for who is
 *  signed in: the auth store is persisted to localStorage, so without a
 *  live Keycloak session a rehydrated user is stale — an old demo-role
 *  session (ENG-278) or one from a closed tab — and is dropped to public. */
export async function initKeycloakAuth(): Promise<void> {
  const mgr = getUserManager();
  let user = mgr ? await mgr.getUser() : null;
  // Access token lapsed while the tab slept — the refresh token may still be
  // inside the Keycloak session window, so try once before signing out.
  if (mgr && user?.expired && user.refresh_token) {
    user = await mgr.signinSilent().catch(() => null);
  }
  useAuthStore.getState().setUser(user && !user.expired ? mapOidcUser(user) : null);
}

/** Redirect to Keycloak's login page. */
export function loginWithKeycloak(): Promise<void> | void {
  return getUserManager()?.signinRedirect();
}

/** Redirect to Keycloak's logout endpoint, ending the SSO session too —
 *  not just our local one. Use this instead of `setUser(null)` for any
 *  session that came from a real Keycloak login (see ProfileMenu), or the
 *  next login attempt will silently re-authenticate against the still-live
 *  Keycloak session without prompting. */
export function logoutFromKeycloak(): Promise<void> | void {
  return getUserManager()?.signoutRedirect();
}

/** True if the current session is a real Keycloak session (vs the mock
 *  role picker) — checked synchronously via cached user state, no network
 *  call. Used by ProfileMenu to decide which sign-out path to take. */
export async function hasKeycloakSession(): Promise<boolean> {
  const mgr = getUserManager();
  if (!mgr) return false;
  const user = await mgr.getUser();
  return user != null && !user.expired;
}

/** `/auth/callback` route handler — completes the Authorization Code
 *  exchange. Throws on failure (invalid state, PKCE mismatch, etc.); the
 *  caller (AuthCallback.tsx) is responsible for surfacing that to the user. */
export async function completeKeycloakCallback(): Promise<void> {
  const mgr = getUserManager();
  if (!mgr) throw new Error('Keycloak auth is not enabled/configured');
  await mgr.signinRedirectCallback();
  // addUserLoaded above already calls setUser — nothing further to do here.
}

/** Loose shape of the claims Keycloak actually puts on the ID token.
 *  `realm_access` and `groups` aren't standard OIDC claims — Keycloak adds
 *  them via default protocol mappers. */
interface KeycloakIdTokenClaims {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  realm_access?: { roles?: string[] };
  groups?: string[];
}

/** `/private-<tenant>/users` or `/private-<tenant>/admins` → `<tenant>`.
 *  Display/identity only, per BE — never used for role gating. */
function tenantFromGroups(groups: string[] | undefined): string | null {
  for (const g of groups ?? []) {
    const m = /^\/private-([^/]+)\/(users|admins)$/.exec(g);
    if (m) return m[1]!;
  }
  return null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function mapOidcUser(user: User): AuthUser {
  const claims = user.profile as unknown as KeycloakIdTokenClaims;
  const roles = claims.realm_access?.roles ?? [];
  const isAdmin = roles.includes(ADMIN_ROLE);
  // Least-privilege default: unrecognised roles fall back to 'user', not
  // 'pilot' — see PILOT_ROLE note in the file header.
  const role = isAdmin ? 'admin' : roles.includes(PILOT_ROLE) ? 'pilot' : 'user';
  const fullName = claims.name ?? claims.preferred_username ?? claims.email ?? claims.sub;

  return {
    id: claims.sub,
    email: claims.email ?? '',
    fullName,
    initials: initials(fullName),
    roles,
    tenantId: tenantFromGroups(claims.groups) ?? config.tenant.id,
    isAdmin,
    role,
  };
}
